import { Button, CloseButton, Combobox, Input, InputProps, useCombobox } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import Text from 'components/shared/Text';
import AbsoluteError from 'components/ui/AbsoluteError';
import { useScrollFocusedFieldOnKeyboardResize } from 'hooks/useScrollFocusedFieldOnKeyboardResize';
import { COUNTRIES, FieldPhoneStore } from 'stores/local/shared/form/FieldPhoneStore';
import { useDevices } from 'utils/devices';

import StatusUnique from './StatusUnique/StatusUnique';
import { countryCodeToFlag, getPlaceholder } from './utils';

import s from './FieldPhone.module.scss';

type FieldPhoneProps = Omit<InputProps, 'value' | 'onChange' | 'disabled' | 'mask' | 'error' | 'onFocus' | 'onBlur'> & {
  store: FieldPhoneStore;
  label?: React.ReactNode;
  placeholder?: string;
  validateOnChange?: boolean;
};

const FieldPhone: React.FC<FieldPhoneProps> = ({ store, label, validateOnChange = false, ...props }) => {
  const { onSearchChange, onSearchClear: onSearchClear, search, selectedCountry, filteredCountries } = store;
  const selectedCountryValue = selectedCountry.value;
  const [digitalValue, setDigitalValue] = React.useState('');
  const prevDigitsRef = React.useRef('');
  const { inputRef, handleFocus } = useScrollFocusedFieldOnKeyboardResize(store);
  const { isPhone } = useDevices();
  const size = isPhone ? 'md' : 'lg';

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      search.change('');
      inputRef.current?.focus();
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const placeholder = props.placeholder ?? getPlaceholder(selectedCountryValue);

  // Синхронизируем digitalValue с store.value при изменении значения извне
  React.useEffect(() => {
    const syncResult = store.syncValueFromExternal();

    if (!syncResult) {
      setDigitalValue('');
      prevDigitsRef.current = '';

      return;
    }

    setDigitalValue(syncResult.formattedValue);
    prevDigitsRef.current = syncResult.nationalNumber;
  }, [store.value, selectedCountryValue, store]);

  const handleDigitalValueChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const result = store.handleInputChange(inputValue, digitalValue, prevDigitsRef.current);

      prevDigitsRef.current = result.digits;
      setDigitalValue(result.formattedValue);

      if (validateOnChange) {
        store.validate();
      }
    },
    [store, digitalValue, validateOnChange],
  );

  const onCountrySelect = React.useCallback(
    (countryCode: string) => {
      const country = COUNTRIES.find((c) => c.code === countryCode);

      if (country) {
        store.selectedCountry.change(country);
        store.setValue('');
        setDigitalValue('');
      }
      combobox.closeDropdown();
    },
    [store, combobox],
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.currentTarget.value);
    },
    [onSearchChange],
  );
  const onToggleDropdown = React.useCallback(() => combobox.toggleDropdown(), [combobox]);

  const countrySelectorButton = (
    <Combobox.EventsTarget>
      <Button
        variant="transparent"
        type="button"
        className={s.countrySelector}
        color="black"
        onClick={onToggleDropdown}
        disabled={store.disabled}
        rightSection={
          <IconChevronDown
            size={16}
            className={classNames(s.chevron, store.disabled && s.chevron_disabled)}
            data-rotated={combobox.dropdownOpened}
          />
        }
      >
        <Text component="span" size="md">
          {selectedCountryValue.dialCode}
        </Text>
      </Button>
    </Combobox.EventsTarget>
  );

  return (
    <Combobox store={combobox} onOptionSubmit={onCountrySelect} offset={8} position="bottom-start">
      <div className={s.fieldPhoneWrapper}>
        <Input.Wrapper
          label={label}
          required={store.required}
          size={size}
          error={store.hasError && <AbsoluteError error={store.error} />}
        >
          <Combobox.DropdownTarget>
            <div>
              <Input
                {...props}
                ref={inputRef}
                onBlur={store.blur}
                onFocus={handleFocus}
                placeholder={props.placeholder ?? placeholder}
                size={size}
                value={digitalValue}
                onChange={handleDigitalValueChange}
                disabled={store.disabled}
                error={store.hasError}
                className={s.fieldPhone}
                leftSection={countrySelectorButton}
                leftSectionPointerEvents="auto"
                rightSection={
                  <StatusUnique isUnique={store.isUnique} isLoading={store.loadingStageUnique?.isLoading ?? false} />
                }
              />
            </div>
          </Combobox.DropdownTarget>
        </Input.Wrapper>
        <Combobox.Dropdown>
          <Combobox.Search
            value={search.value}
            onChange={handleSearchChange}
            placeholder="Поиск страны"
            rightSectionPointerEvents={search.value.length > 0 ? 'auto' : undefined}
            rightSection={search.value.length > 0 && <CloseButton size="sm" onClick={onSearchClear} />}
          />
          <Combobox.Options className={s.options}>
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <Combobox.Option
                  key={country.code}
                  value={country.code}
                  className={s.option}
                  data-selected={country.code === selectedCountryValue.code}
                >
                  <Text component="span" className={s.countryFlag}>
                    {countryCodeToFlag(country.code)}
                  </Text>
                  <Text component="span" size="md" className={s.countryName}>
                    {country.name}
                  </Text>
                  <Text component="span" size="sm" color="gray.6" className={s.countryDialCode}>
                    {country.dialCode}
                  </Text>
                </Combobox.Option>
              ))
            ) : (
              <Combobox.Empty>Ничего не найдено</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </div>
    </Combobox>
  );
};

export default observer(FieldPhone);
